import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import TableBadge from '@spark/ui-components/components/TanStackTable/components/TableBadge';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import Tooltip from '@spark/ui-components/components/Tooltip';
import TempoEsperaDescargaCell, {
  TempoEsperaBadge,
  formatTempoEspera,
  getTempoEsperaVariant,
  hasChegadaDescarga,
  parseTempoEsperaSeconds
} from './TempoEsperaDescargaCell';

const renderText = (maxLength = 18) => ({ getValue }) => (
  <TruncatedText value={getValue() || '-'} maxLength={maxLength} />
);

const CargaViagemCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

const TempoEsperaDescargaStaticCell = ({ getValue, row }) => {
  if (!hasChegadaDescarga(row)) {
    return (
      <TempoEsperaBadge $variant="empty">
        --/--/--
      </TempoEsperaBadge>
    );
  }

  const totalSeconds = parseTempoEsperaSeconds(getValue());

  if (totalSeconds === null) {
    return <TruncatedText value="-" maxLength={18} />;
  }

  return (
    <TempoEsperaBadge $variant={getTempoEsperaVariant(totalSeconds)}>
      {formatTempoEspera(totalSeconds)}
    </TempoEsperaBadge>
  );
};

const TempoAtrasadoCell = ({ getValue, row }) => {
  const value = getValue();
  const hasValue = value !== null && value !== undefined && String(value).trim() !== '' && String(value).trim() !== '-';
  const totalSeconds = useMemo(() => parseTempoEsperaSeconds(value), [value]);
  const [elapsedSeconds, setElapsedSeconds] = useState(totalSeconds);
  const shouldCount = !hasChegadaDescarga(row) && totalSeconds > 0;

  useEffect(() => {
    setElapsedSeconds(totalSeconds);

    if (!shouldCount) return undefined;

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(totalSeconds + Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [shouldCount, totalSeconds]);

  if (!hasValue) {
    return (
      <TempoEsperaBadge $variant="empty">
        --/--/--
      </TempoEsperaBadge>
    );
  }

  if (totalSeconds === null || totalSeconds === 0) {
    return (
      <TempoEsperaBadge $variant="empty">
        --/--/--
      </TempoEsperaBadge>
    );
  }

  return (
    <TempoEsperaBadge $variant="critical">
      {formatTempoEspera(elapsedSeconds)}
    </TempoEsperaBadge>
  );
};

const renderStatus = ({ getValue }) => {
  const value = getValue() || '-';
  const normalized = String(value).toLowerCase();
  const variant = normalized.includes('aguardando')
    ? 'danger'
    : normalized.includes('conclu')
      ? 'success'
      : 'default';

  return <TableBadge label={value} variant={variant} />;
};

const isAtivoValue = (value) => {
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() !== 'false';
};

export const getDescargasColumns = (onAuditoria, loadingAcao, onLocalizacao, onAlterarStatus, options = {}) => {
  const { onDetalhes, onEditarRapida, onExcluirManual } = options;
  const columns = [{
    id: 'numeroCarga',
    accessorKey: 'numeroCarga',
    header: 'Carga (Viagem)',
    size: 140,
    cell: ({ getValue, row }) => (
      <CargaViagemCell>
        <TruncatedText value={getValue() || '-'} maxLength={12} />
        {row?.original?.isDescargaManual ? (
          <TableBadge label="Descarga manual" variant="warning" />
        ) : null}
      </CargaViagemCell>
    )
  },
  {
    id: 'cargasCTE',
    accessorKey: 'cargasCTE',
    header: 'Cargas CTE',
    size: 220,
    cell: renderText(32)
  },
  {
    id: 'protocoloCarga',
    accessorKey: 'protocoloCarga',
    header: 'Protocolo Carga',
    size: 140,
    cell: renderText(14)
  },
  {
    id: 'codigoCarga',
    accessorKey: 'codigoCarga',
    header: 'Cód. Carga',
    size: 120,
    cell: renderText(12)
  },
  {
    id: 'situacaoCarga',
    accessorKey: 'situacaoCarga',
    header: 'Situacao Carga',
    size: 210,
    cell: renderStatus
  },
  {
    id: 'descricaoTipoOperacao',
    accessorKey: 'descricaoTipoOperacao',
    header: 'Tipo Operação',
    size: 200,
    cell: renderText(24)
  },
  {
    id: 'numeroPedido',
    accessorKey: 'numeroPedido',
    header: 'Pedido',
    size: 130,
    cell: renderText(14)
  },
  {
    id: 'protocoloPedido',
    accessorKey: 'protocoloPedido',
    header: 'Protocolo Pedido',
    size: 150,
    cell: renderText(14)
  },
  {
    id: 'codigoPedido',
    accessorKey: 'codigoPedido',
    header: 'Cód. Pedido',
    size: 120,
    cell: renderText(12)
  },
  {
    id: 'dataAgendamentoPedido',
    accessorKey: 'dataAgendamentoPedido',
    header: 'Agendamento Pedido',
    size: 170,
    cell: renderText(18)
  },
  {
    id: 'quantidadePaletes',
    accessorKey: 'quantidadePaletes',
    header: 'Qtd. Paletes/Gaiolas',
    size: 170,
    cell: renderText(10)
  },
  {
    id: 'placaVeiculo',
    accessorKey: 'placaVeiculo',
    header: 'Placa Veiculo',
    size: 130,
    cell: renderText(12)
  },
  {
    id: 'numeroFrota',
    accessorKey: 'numeroFrota',
    header: 'Frota',
    size: 120,
    cell: renderText(12)
  },
  {
    id: 'tipoVeiculo',
    accessorKey: 'tipoVeiculo',
    header: 'Tipo Veículo',
    size: 130,
    cell: renderText(12)
  },
  {
    id: 'descricaoModeloVeicular',
    accessorKey: 'descricaoModeloVeicular',
    header: 'Modelo Veicular',
    size: 170,
    cell: renderText(22)
  },
  {
    id: 'nomeRemetente',
    accessorKey: 'nomeRemetente',
    header: 'Remetente',
    size: 220,
    cell: renderText(28)
  },
  {
    id: 'cnpjRemetente',
    accessorKey: 'cnpjRemetente',
    header: 'CNPJ Remetente',
    size: 160,
    cell: renderText(18)
  },
  {
    id: 'nomeDestinatario',
    accessorKey: 'nomeDestinatario',
    header: 'Destinatário',
    size: 220,
    cell: renderText(28)
  },
  {
    id: 'cnpjDestinatario',
    accessorKey: 'cnpjDestinatario',
    header: 'CNPJ Destinatario',
    size: 170,
    cell: renderText(18)
  },
  {
    id: 'loja',
    accessorKey: 'loja',
    header: 'Loja',
    size: 90,
    cell: renderText(8)
  },
  {
    id: 'lojaRemetente',
    accessorKey: 'lojaRemetente',
    header: 'Loja Remetente',
    size: 140,
    cell: renderText(8)
  },
  {
    id: 'cidade',
    accessorKey: 'cidade',
    header: 'Cidade',
    size: 150,
    cell: renderText(18)
  },
  {
    id: 'ufDestinatario',
    accessorKey: 'ufDestinatario',
    header: 'UF',
    size: 70,
    cell: renderText(4)
  },
  {
    id: 'nomeTomador',
    accessorKey: 'nomeTomador',
    header: 'Tomador',
    size: 200,
    cell: renderText(24)
  },
  {
    id: 'cnpjTomador',
    accessorKey: 'cnpjTomador',
    header: 'CNPJ Tomador',
    size: 160,
    cell: renderText(18)
  },
  {
    id: 'placasReboque',
    accessorKey: 'placasReboque',
    header: 'Placas Reboque',
    size: 160,
    cell: renderText(20)
  },
  {
    id: 'frotasReboque',
    accessorKey: 'frotasReboque',
    header: 'Frotas Reboque',
    size: 160,
    cell: renderText(20)
  },
  {
    id: 'modelosReboque',
    accessorKey: 'modelosReboque',
    header: 'Modelos Reboque',
    size: 170,
    cell: renderText(22)
  },
  {
    id: 'dataCriacaoPedido',
    accessorKey: 'dataCriacaoPedido',
    header: 'Criação Pedido',
    size: 170,
    cell: renderText(18)
  },
  {
    id: 'dataCriacaoCarga',
    accessorKey: 'dataCriacaoCarga',
    header: 'Criacao Carga',
    size: 170,
    cell: renderText(18)
  },
  {
    id: 'dataCarregamentoPedido',
    accessorKey: 'dataCarregamentoPedido',
    header: 'Carregamento Pedido',
    size: 180,
    cell: renderText(18)
  },
  {
    id: 'infoMotorista',
    accessorKey: 'infoMotorista',
    header: 'Motorista',
    size: 220,
    cell: renderText(30)
  },
  {
    id: 'nomeResponsavel',
    accessorKey: 'nomeResponsavel',
    header: 'Gestor',
    size: 220,
    cell: renderText(28)
  },
  {
    id: 'dataAgendamentoDescarga',
    accessorKey: 'dataAgendamentoDescarga',
    header: 'Data Agenda',
    size: 160,
    cell: renderText(18)
  },
  {
    id: 'chegadaDescarga',
    accessorKey: 'chegadaDescarga',
    header: 'Data Chegada',
    size: 160,
    cell: renderText(18)
  },
  {
    id: 'saidaDescarga',
    accessorKey: 'saidaDescarga',
    header: 'Saída',
    size: 150,
    cell: renderText(18)
  },
  {
    id: 'tempoEsperaDescarga',
    accessorKey: 'tempoEsperaDescarga',
    header: 'Tempo Espera Descarga',
    size: 180,
    cell: options.staticTempoEspera ? TempoEsperaDescargaStaticCell : TempoEsperaDescargaCell
  },
  {
    id: 'inicioContagem',
    accessorKey: 'inicioContagem',
    header: 'Início da contagem',
    size: 170,
    cell: renderText(18)
  },
  {
    id: 'tempoForaJanela',
    accessorKey: 'tempoForaJanela',
    header: 'Fora da janela',
    size: 140,
    cell: renderText(12)
  },
  {
    id: 'janelaAplicada',
    accessorKey: 'janelaAplicada',
    header: 'Janela aplicada',
    size: 180,
    cell: renderText(24)
  },
  {
    id: 'tempoAtrasado',
    accessorKey: 'tempoAtrasado',
    header: 'Tempo Atraso',
    size: 150,
    cell: TempoAtrasadoCell
  },
  {
    id: 'observacoesDescarga',
    accessorKey: 'observacoesDescarga',
    header: 'Observações Descarga',
    size: 230,
    cell: renderText(32)
  },
  {
    id: 'descricaoPosicaoAngellira',
    accessorKey: 'descricaoPosicaoAngellira',
    header: 'Posição Angellira',
    size: 190,
    cell: renderText(24)
  },
  {
    id: 'codigoViagemAngellira',
    accessorKey: 'codigoViagemAngellira',
    header: 'Cód. Viagem Angellira',
    size: 190,
    cell: renderText(18)
  },
  {
    id: 'statusViagemAngellira',
    accessorKey: 'statusViagemAngellira',
    header: 'Status Viagem Angellira',
    size: 180,
    cell: renderText(22)
  },
  {
    id: 'clienteDistanciaAngellira',
    accessorKey: 'clienteDistanciaAngellira',
    header: 'Distância Cliente',
    size: 150,
    cell: renderText(16)
  },
  {
    id: 'clienteTempoPrevChegadaAngellira',
    accessorKey: 'clienteTempoPrevChegadaAngellira',
    header: 'Tempo Prev. Chegada',
    size: 170,
    cell: renderText(18)
  }
  ];

  if (onAuditoria || onDetalhes || onEditarRapida || onAlterarStatus || onExcluirManual) {
    columns.unshift({
      id: 'acoes',
      header: 'Ações',
      accessorKey: 'id',
      size: 210,
      enableHiding: true,
      enablePinning: true,
      enableSorting: false,
      enableCopy: false,
      cell: ({ row }) => (
        <div className="d-inline-flex align-items-center" style={{ gap: 6 }}>
          {onDetalhes && !row.original?.isDescargaManual ? (
            <Tooltip dir="top" text="Abrir detalhes">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-secondary"
                  size="sm"
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDetalhes(row.original);
                  }}
                >
                  <Icon name="eye" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          ) : null}
          {onEditarRapida ? (
            <Tooltip dir="top" text={row.original?.isDescargaManual ? 'Editar descarga manual' : 'Editar datas da descarga'}>
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-primary"
                  size="sm"
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEditarRapida(row.original);
                  }}
                >
                  <Icon name="pencil" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          ) : null}
          {!row.original?.isDescargaManual && onAuditoria ? (
            <Tooltip dir="top" text="Visualizar auditoria">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-info"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'auditoria'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'auditoria'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAuditoria(row.original);
                  }}
                >
                  <Icon name="file-document-outline" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          ) : null}
          {!row.original?.isDescargaManual && onAlterarStatus ? (
            <Tooltip dir="top" text={isAtivoValue(row.original?.ativo) ? 'Inativar descarga' : 'Ativar descarga'}>
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant={isAtivoValue(row.original?.ativo) ? 'outline-danger' : 'outline-success'}
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'status'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'status'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAlterarStatus(row.original);
                  }}
                >
                  <Icon name={isAtivoValue(row.original?.ativo) ? 'close-circle-outline' : 'check-circle-outline'} size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          ) : null}
          {row.original?.isDescargaManual && onExcluirManual ? (
            <Tooltip dir="top" text="Excluir descarga manual">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-danger"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'excluir-manual'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'excluir-manual'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onExcluirManual(row.original);
                  }}
                >
                  <Icon name="delete" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          ) : null}
        </div>
      )
    });
  }

  if (onLocalizacao) {
    columns.unshift({
      id: 'localizacao',
      header: 'Localização',
      accessorKey: 'id',
      size: 120,
      enableHiding: true,
      enablePinning: true,
      enableSorting: false,
      enableCopy: false,
      cell: ({ row }) => {
        if (row?.original?.isDescargaManual) return <span>-</span>;

        return (
        <Tooltip dir="top" text="Visualizar localização">
          <span className="d-inline-block">
            <ButtonWithLoading
              variant="outline-primary"
              size="sm"
              loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'localizacao'}
              disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'localizacao'}
              loadingText=""
              style={{ minWidth: 'auto' }}
              onClick={(event) => {
                event.stopPropagation();
                onLocalizacao(row.original);
              }}
            >
              <Icon name="map-marker-radius" size={14} />
            </ButtonWithLoading>
          </span>
        </Tooltip>
        );
      }
    });
  }

  return columns;
};
